import { Auth0Client } from '@auth0/auth0-spa-js';
import { logger } from '@/lib/logger';
import type { Issuer } from '@/lib/auth';

export type IssuerProbeConfig = {
  issuer: Issuer;
  domain: string;
  clientId: string;
};

// Errors that only mean "no interactive session for this tenant yet"; not an
// actual failure, so they fall through to the next issuer in silence.
const NO_SESSION = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
  'access_denied',
]);

// Probes the given issuers in order (paid first, then free: a user signed into
// both is a paid user, and upgrading blocks the free tenant's login) for an
// existing Auth0 session via a silent prompt=none flow, and returns the first
// that has one, or null. Each Auth0Client's options must equal this app's
// Auth0Provider options so the cache key the probe writes is the one the
// provider that mounts next reads back.
export async function probeSession(configs: IssuerProbeConfig[]): Promise<Issuer | null> {
  for (const config of configs) {
    const client = new Auth0Client({
      domain: config.domain,
      clientId: config.clientId,
      authorizationParams: { redirect_uri: window.location.origin },
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
      cacheLocation: 'localstorage',
      authorizeTimeoutInSeconds: 10,
    });
    try {
      await client.getTokenSilently();
      return config.issuer;
    } catch (err) {
      const code =
        typeof err === 'object' && err !== null && 'error' in err ? String(err.error) : '';
      if (!NO_SESSION.has(code)) {
        // The error carries no email, so logging it is safe.
        logger.error('sessionProbe failed', err);
      }
    }
  }
  return null;
}

// A probe success whose provider still ends up unauthenticated sends the app back
// to the login screen; probing once per page load turns that into the email form
// instead of a probe loop.
let hasClaimedSessionProbe = false;

export function claimSessionProbe(): boolean {
  if (hasClaimedSessionProbe) return false;
  hasClaimedSessionProbe = true;
  return true;
}
